import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  BookOpen,
  GraduationCap,
  ChevronRight,
  Mail,
  Send,
  Loader2,
  User,
  Building2,
  Quote,
  Scale,
  Users,
  Newspaper,
  BriefcaseBusiness,
  Sparkles,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ThemeToggle from "@/components/ThemeToggle";

const PURPOSES = [
  { value: "partnership", label: "Partenariat institutionnel", icon: BriefcaseBusiness },
  { value: "research", label: "Collaboration de recherche", icon: GraduationCap },
  { value: "teaching", label: "Usage pédagogique / enseignement", icon: Users },
  { value: "press", label: "Presse & médias", icon: Newspaper },
  { value: "other", label: "Autre", icon: Mail },
];

export default function About() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    purpose: "research",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      form.message.trim().length < 10
    ) {
      toast.error("Veuillez remplir les champs requis (message ≥ 10 caractères).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        purpose: form.purpose,
        message: form.message.trim(),
      });
      toast.success("Message envoyé. L'équipe VITAE-PUBLICA™ vous répondra.");
      setSent(true);
      setForm({
        name: "",
        email: "",
        organization: "",
        purpose: "research",
        message: "",
      });
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || e2?.message || "Envoi impossible. Réessayez.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="about-page"
    >
      {/* Sticky header */}
      <div className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="flex items-center gap-2 font-judicial text-xl font-black tracking-tight text-brand-blue dark:text-brand-gold"
            data-testid="about-home-link"
          >
            <Sparkles className="h-5 w-5" />
            VITAE-PUBLICA™
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Accueil
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="default" size="sm" data-testid="about-cta-register">
                Créer un compte
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-border/40 bg-gradient-to-b from-brand-blue/10 via-background to-background py-16 sm:py-24"
        data-testid="about-hero"
      >
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
            <BookOpen className="h-3.5 w-3.5" />
            À propos de la théorie vitaliste
          </div>
          <h1 className="mt-4 font-judicial text-4xl font-black leading-tight tracking-tight sm:text-6xl">
            Les finances publiques sont{" "}
            <span className="text-brand-gold">des tranches de vies</span>.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            La Nouvelle Théorie des Finances Publiques propose de mesurer
            l'action publique non plus en monnaie, mais en{" "}
            <strong className="text-foreground">vie humaine</strong>. Chaque
            prélèvement est une tranche d'existence, chaque dette une hypothèque
            sur l'avenir.
          </p>
        </div>
      </section>

      {/* Quote block */}
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
        <blockquote className="rounded-xl border border-brand-gold/30 bg-brand-gold/5 p-8 text-left">
          <Quote className="h-7 w-7 text-brand-gold" />
          <p className="mt-3 font-judicial text-xl italic leading-relaxed text-foreground sm:text-2xl">
            « L'unité de compte des finances publiques n'est pas la monnaie, mais
            la vie humaine. Chaque impôt est une tranche d'existence arrachée au
            contribuable ; chaque dette publique, une hypothèque silencieuse sur
            la vie future des générations à venir. »
          </p>
          <footer className="mt-5 text-sm text-muted-foreground">
            — <strong className="text-foreground">Ahmed ELY Mustapha</strong>,{" "}
            <em>
              La Nouvelle Théorie des Finances Publiques — Les Finances Publiques
              sont des Tranches de Vies
            </em>
          </footer>
        </blockquote>
      </section>

      {/* Core formula */}
      <section
        className="border-y border-border/40 bg-muted/30 py-14"
        data-testid="about-formula"
      >
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Formule vitaliste canonique
          </div>
          <div className="mx-auto mt-4 max-w-2xl rounded-lg border-2 border-brand-blue/30 bg-background p-6 font-mono text-2xl font-bold text-brand-blue dark:border-brand-gold/30 dark:text-brand-gold sm:text-3xl">
            Jours<sub className="text-base">vie</sub> ={" "}
            <span className="mx-1">
              Prélèvements ÷ (Salaire<sub className="text-base">mensuel</sub> / 30)
            </span>
          </div>
          <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground">
            Elle convertit tout flux fiscal ou budgétaire en jours d'existence
            humaine du contribuable de référence. Universelle, auditable,
            pédagogique.
          </p>
        </div>
      </section>

      {/* Author bio */}
      <section
        className="mx-auto max-w-6xl px-4 py-14 sm:px-6"
        data-testid="about-author"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <GraduationCap className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
                Auteur de la méthodologie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed">
              <div>
                <div className="font-judicial text-lg font-bold text-foreground">
                  Ahmed ELY Mustapha
                </div>
                <div className="text-xs text-muted-foreground">
                  Penseur, chercheur et théoricien des finances publiques
                </div>
              </div>
              <p>
                Auteur de la{" "}
                <em>
                  Nouvelle Théorie des Finances Publiques — Les Finances Publiques
                  sont des Tranches de Vies
                </em>
                , une proposition épistémologique qui substitue la vie humaine à
                la monnaie comme unité de compte des politiques budgétaires,
                fiscales et de la dette souveraine.
              </p>
              <p>
                Cette théorie fonde l'<strong>analyse vitaliste</strong> de
                l'action publique : l'État ne dépense pas de l'argent ; il consomme
                des tranches de vie de ses contribuables. Chaque décision
                budgétaire devient ainsi arbitrable en années d'existence.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BriefcaseBusiness className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
                Programmeur de l'application
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed">
              <div>
                <div className="font-judicial text-lg font-bold text-foreground">
                  ELY Mustapha
                </div>
                <div className="text-xs text-muted-foreground">
                  PMP · I-PMP · IBM Full Stack Developer
                </div>
              </div>
              <p>
                Concepteur et développeur de VITAE-PUBLICA™ — plateforme
                d'investigation, d'audit et d'évaluation vitaliste des finances
                publiques. L'application matérialise la théorie en un outil
                quantitatif ouvert aux chercheurs, journalistes, magistrats et
                décideurs publics.
              </p>
              <p className="text-xs italic text-muted-foreground">
                Toutes les analyses générées portent la mention pédagogique
                obligatoire et n'ont aucune valeur juridictionnelle.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Book & uses */}
      <section className="border-t border-border/40 bg-muted/20 py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
                  Chercheurs
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Publiez des analyses vitalistes reproductibles, comparez
                plusieurs exercices fiscaux ou pays, exportez des rapports
                citables (PDF, DOCX, XLSX).
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Newspaper className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
                  Journalistes
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Traduisez les budgets publics en jours de vie perdus, générez
                des infographies haute résolution pour vos enquêtes, partagez
                un lien sécurisé à vos rédactions.
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Scale className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
                  Universités & magistrats
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Support pédagogique en droit pénal des affaires, analyse
                juridique des infractions aux finances publiques, simulation de
                jugement vitaliste à titre strictement illustratif.
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section
        className="mx-auto max-w-3xl px-4 py-14 sm:px-6"
        data-testid="about-contact"
        id="contact"
      >
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
            <Mail className="h-3.5 w-3.5" />
            Partenariats & contact
          </div>
          <h2 className="mt-3 font-judicial text-3xl font-black sm:text-4xl">
            Collaborons ensemble
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Vous êtes une chaire universitaire, une rédaction, une cour des
            comptes, une ONG de transparence budgétaire ? Écrivez-nous.
          </p>
        </div>

        <Card className="mt-8">
          <CardContent className="pt-6">
            {sent && (
              <div
                className="mb-4 rounded-md border border-emerald-400/40 bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
                data-testid="contact-success"
              >
                Votre message a été transmis. L'équipe VITAE-PUBLICA™ vous
                répondra par email sous 5 jours ouvrés.
              </div>
            )}
            <form
              onSubmit={onSubmit}
              className="space-y-4"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="contact-name">Nom et prénom *</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="contact-name"
                      required
                      minLength={2}
                      maxLength={120}
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      className="pl-9"
                      data-testid="contact-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-email">Email *</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      maxLength={200}
                      value={form.email}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, email: e.target.value }))
                      }
                      className="pl-9"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="contact-org">Organisation</Label>
                  <div className="relative mt-1.5">
                    <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="contact-org"
                      maxLength={200}
                      value={form.organization}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, organization: e.target.value }))
                      }
                      className="pl-9"
                      placeholder="Université, média, ministère…"
                      data-testid="contact-org-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Objet *</Label>
                  <Select
                    value={form.purpose}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, purpose: v }))
                    }
                  >
                    <SelectTrigger
                      className="mt-1.5"
                      data-testid="contact-purpose-trigger"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PURPOSES.map((p) => (
                        <SelectItem
                          key={p.value}
                          value={p.value}
                          data-testid={`contact-purpose-${p.value}`}
                        >
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="contact-message">Message *</Label>
                <Textarea
                  id="contact-message"
                  required
                  minLength={10}
                  maxLength={4000}
                  rows={5}
                  value={form.message}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, message: e.target.value }))
                  }
                  placeholder="Décrivez votre projet, votre besoin de collaboration ou votre question…"
                  className="mt-1.5"
                  data-testid="contact-message-input"
                />
                <div className="mt-1 text-right text-[11px] text-muted-foreground">
                  {form.message.length}/4000
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs italic text-muted-foreground">
                  Vos données ne sont utilisées que pour vous répondre.
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Envoi…
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-border/40 bg-muted/30 py-10">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 px-4 text-center sm:px-6">
          <h3 className="font-judicial text-2xl font-black">
            Prêt à auditer un budget public en tranches de vies ?
          </h3>
          <p className="text-sm text-muted-foreground">
            VITAE-PUBLICA™ est gratuit pour la recherche et l'enseignement.
          </p>
          <Link to="/register">
            <Button size="lg" data-testid="about-footer-register">
              Créer mon compte
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Legal footer */}
      <div className="border-t border-border/40 py-6 text-center text-xs text-muted-foreground">
        VITAE-PUBLICA™ — Document pédagogique sans valeur juridictionnelle.
        Méthodologie : Ahmed ELY Mustapha. Développement : ELY Mustapha — PMP ·
        I-PMP · IBM Full Stack Developer.
      </div>
    </div>
  );
}
